import {
  EmploymentContractAgreementDetail,
  EmploymentContractField
} from './EmploymentContractService';

export type EmploymentContractCloneLoadState = 'loading' | 'ready' | 'error';

export type EmploymentContractCloneErrorCode =
  | 'SOURCE_UNAVAILABLE'
  | 'SOURCE_DATA_EMPTY'
  | 'TEMPLATE_UNAVAILABLE'
  | 'CURRENT_EMPLOYER_UNAVAILABLE'
  | 'UNKNOWN';

export type EmploymentContractCloneSafeError = {
  code: EmploymentContractCloneErrorCode;
  message: string;
};

export type EmploymentContractCloneSeed = {
  sourceAgreementName: string;
  sourceAgreementNo: string;
  sourceStatusId: string;
  templateCode: string;
  agreementName: string;
  formData: Record<string, any>;
  employerSigner: Record<string, any>;
  employeeSigner: Record<string, any>;
  signaturePositions: any[];
};

const EMPLOYER_SIGNER_KEYS = [
  'partyId', 'partyName', 'emailAddress', 'contactNumber', 'identityCard',
  'representativeName', 'representativeTitle', 'representativeNationality',
  'address', 'pseudoId', 'partyTypeEnumId'
] as const;

const AGREEMENT_PARTY_KEYS = [
  ...EMPLOYER_SIGNER_KEYS, 'roleTypeId', 'sequenceNum', 'signingOrder'
] as const;

const SIGNATURE_POSITION_KEYS = [
  'partyId', 'roleTypeId', 'locationSeqId', 'signatureTypeEnumId', 'pageNumber',
  'pageWidth', 'pageHeight', 'widthPx', 'heightPx', 'ulx', 'uly', 'fontSize',
  'partyName', 'identityCard', 'emailAddress', 'contactNumber', 'representativeName'
] as const;

const copyKeys = (value: Record<string, any> = {}, keys: readonly string[]) => keys.reduce((result, key) => {
  if (value[key] !== undefined) result[key] = value[key];
  return result;
}, {} as Record<string, any>);

export const emptyCloneEmployeeSigner = () => ({
  partyId: '',
  partyName: '',
  emailAddress: '',
  contactNumber: '',
  roleTypeId: 'AgreementSigner',
  sequenceNum: 2
});

export const buildSafeCloneName = (sourceName?: string) => {
  const normalizedName = String(sourceName || '').trim() || 'Hợp đồng lao động';
  return `Bản sao - ${normalizedName}`;
};

export const hasCloneSourceData = (data: unknown): data is Record<string, any> =>
  Boolean(data && typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length > 0);

export const initializeEmploymentContractClone = (
  source: EmploymentContractAgreementDetail,
  fields: EmploymentContractField[],
  currentEmployer: Record<string, any>
): EmploymentContractCloneSeed => {
  const agreement = source.agreement || { agreementId: '' };
  const sourceData = source.data || {};
  const formData = fields.reduce((result, field) => {
    if (field.fieldGroup === 'employee') {
      result[field.fieldName] = '';
      return result;
    }
    const sourceValue = sourceData[field.fieldName];
    result[field.fieldName] = sourceValue === null || sourceValue === undefined
      ? (field.defaultValue ?? '')
      : sourceValue;
    return result;
  }, {} as Record<string, any>);

  return {
    sourceAgreementName: agreement.agreementName || '',
    sourceAgreementNo: agreement.agreementNo || '',
    sourceStatusId: agreement.statusId || '',
    templateCode: source.templateCode || '',
    agreementName: buildSafeCloneName(agreement.agreementName),
    formData,
    employerSigner: {
      ...copyKeys(currentEmployer, EMPLOYER_SIGNER_KEYS),
      roleTypeId: 'AgreementSigner',
      sequenceNum: 1
    },
    employeeSigner: emptyCloneEmployeeSigner(),
    signaturePositions: []
  };
};

export const sanitizeEmploymentContractClonePayload = (
  payload: Record<string, any>,
  fields: EmploymentContractField[]
) => {
  const allowedFields = new Set(fields.map(field => field.fieldName));
  const sourceData = payload.data && typeof payload.data === 'object' ? payload.data : {};
  const data = Object.keys(sourceData).reduce((result, fieldName) => {
    if (allowedFields.has(fieldName)) result[fieldName] = sourceData[fieldName];
    return result;
  }, {} as Record<string, any>);

  return {
    agreementName: payload.agreementName || '',
    signingModeEnumId: payload.signingModeEnumId || 'AgtSignSequential',
    isUrgent: payload.isUrgent || 'N',
    ...(payload.fromDate ? { fromDate: payload.fromDate } : {}),
    data,
    agreementParties: (Array.isArray(payload.agreementParties) ? payload.agreementParties : [])
      .map((party: Record<string, any>) => copyKeys(party, AGREEMENT_PARTY_KEYS)),
    signaturePositions: (Array.isArray(payload.signaturePositions) ? payload.signaturePositions : [])
      .map((position: Record<string, any>) => copyKeys(position, SIGNATURE_POSITION_KEYS))
  };
};

export const getEmploymentContractCloneSafeError = (
  code: EmploymentContractCloneErrorCode
): EmploymentContractCloneSafeError => {
  const messages: Record<EmploymentContractCloneErrorCode, string> = {
    SOURCE_UNAVAILABLE: 'Không thể truy cập hợp đồng nguồn để nhân bản.',
    SOURCE_DATA_EMPTY: 'Không thể nhân bản hợp đồng này vì không có dữ liệu biểu mẫu.',
    TEMPLATE_UNAVAILABLE: 'Mẫu của hợp đồng nguồn không còn khả dụng.',
    CURRENT_EMPLOYER_UNAVAILABLE: 'Không xác định được đơn vị sử dụng lao động hiện tại.',
    UNKNOWN: 'Không thể chuẩn bị bản nhân bản. Vui lòng thử lại.'
  };
  return { code, message: messages[code] };
};

export const classifyEmploymentContractCloneError = (error: any): EmploymentContractCloneSafeError => {
  const explicitCode = error?.cloneErrorCode as EmploymentContractCloneErrorCode | undefined;
  if (explicitCode && [
    'SOURCE_UNAVAILABLE', 'SOURCE_DATA_EMPTY', 'TEMPLATE_UNAVAILABLE',
    'CURRENT_EMPLOYER_UNAVAILABLE', 'UNKNOWN'
  ].includes(explicitCode)) {
    return getEmploymentContractCloneSafeError(explicitCode);
  }
  const status = Number(error?.response?.status || 0);
  if (status === 401 || status === 403 || status === 404) {
    return getEmploymentContractCloneSafeError('SOURCE_UNAVAILABLE');
  }
  return getEmploymentContractCloneSafeError('UNKNOWN');
};

export const createEmploymentContractCloneError = (code: EmploymentContractCloneErrorCode) => ({
  cloneErrorCode: code
});
